/* eslint-disable react/jsx-props-no-spreading */
import React, { SetStateAction, Dispatch, useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import TextField from '@material-ui/core/TextField';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { normalizeBrandsDataFromQuery, showBrandNameWithDepth } from 'utils/brands';
import { LIST_BRANDS_WITH_CHILDREN, ADD_BRAND } from '../../../../queries/products/brand';

const filter = createFilterOptions<IBrand | IAddOption>();

function isAddOption(option: IBrand | IAddOption): option is IAddOption {
  if ((option as IAddOption).label) {
    return true;
  }

  return false;
}

interface IAddOption {
  label: string;
  value: string;
}

interface ISelectBrandsProps {
  brandId: string | null;
  setBrandId: Dispatch<SetStateAction<string | null>>;
  brandsFetchPolicy: 'cache-and-network' | 'cache-first';
  label?: string;
}

export const SelectBrands: React.FC<ISelectBrandsProps> = ({ brandId, setBrandId, brandsFetchPolicy, label = 'Brand' }) => {
  const [creatingNewBrand, setCreatingNewBrand] = useState(false);

  const [internalBrand, setInternalBrand] = useState<IBrand | IAddOption | null>(null);

  const queryBrands = useQuery(LIST_BRANDS_WITH_CHILDREN, { fetchPolicy: brandsFetchPolicy });
  const [addBrandMutation] = useMutation(ADD_BRAND);

  useEffect(() => {
    const normalizedBrandList = normalizeBrandsDataFromQuery(queryBrands?.data?.brandsWithChildren);
    const foundBrand = normalizedBrandList.find((brandOnList: IBrand) => brandOnList.id === brandId) || null;
    setInternalBrand(foundBrand);
  }, [brandId, queryBrands]);

  const addBrand = async (brandName: string) => {
    setCreatingNewBrand(true);
    const result = await addBrandMutation({
      variables: {
        name: brandName,
      },
    });

    if (result?.data?.createBrand) {
      setBrandId(result.data.createBrand?.id);
      queryBrands.refetch();
    }
    setCreatingNewBrand(false);
  };

  const handleChange = (_: unknown, newValue: string | IBrand | IAddOption | null) => {
    if (newValue) {
      if (typeof newValue === 'string') {
        addBrand(newValue);
        setBrandId(null);
      } else if (isAddOption(newValue)) {
        addBrand(newValue.value);
        setBrandId(null);
      } else {
        setBrandId(newValue.id);
      }
    }
  };

  const handleChangeInput = (_: unknown, newValue: string) => {
    const normalizedBrandList = normalizeBrandsDataFromQuery(queryBrands?.data?.brandsWithChildren);
    const brand = normalizedBrandList.find((brand: IBrand) => newValue === brand.name);
    if (brand) setBrandId(brand.id);
  };

  if (queryBrands.loading) return <>Loading...</>;
  if (queryBrands.error) return <>{`Error! ${queryBrands.error.message}`}</>;

  return (
    <>
      <Autocomplete
        value={internalBrand}
        onChange={handleChange}
        onInputChange={handleChangeInput}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (
            params.inputValue !== '' &&
            !options.some(option => {
              if (isAddOption(option)) return false;
              return option.name === params.inputValue;
            })
          ) {
            filtered.push({
              label: `Add "${params.inputValue}"`,
              value: params.inputValue,
            });
          }

          return filtered;
        }}
        disabled={creatingNewBrand}
        options={normalizeBrandsDataFromQuery(queryBrands?.data?.brandsWithChildren) as (IBrand | IAddOption)[] || []}
        getOptionLabel={(option) => {
          if (option !== null && option !== undefined) {
            if (isAddOption(option)) {
              return option.label;
            }
            return showBrandNameWithDepth(option);
          }
          return '';
        }}
        renderInput={(params) => (
          <TextField {...params} label={label} />
        )}
        freeSolo
        selectOnFocus
        handleHomeEndKeys
      />
    </>
  );
};
